import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import * as THREE from "three";
import { patrolHotspotWorldPosition } from "./patrolHotspotProjection.js";
import { projectPatrolWorldToHostPx } from "./patrolPanoramaFocusRing.js";
import { getPatrolHotspotMarkerIconUrl } from "./patrolHotspotIcons.js";
import {
  applyPatrolHotspotCircleMarkerStyle,
  createPatrolHotspotIconImg,
  PATROL_API_URL_MARKER_HEIGHT_PX,
} from "./patrolHotspotMarkerDom.js";
import {
  getConfiguredApiUrlHotspotLanes,
  patrolApiUrlMarkerWidth,
} from "./patrolApiUrlHotspot.js";
import { PatrolApiUrlHotspotToggle } from "./PatrolApiUrlHotspotToggle.jsx";
import { PatrolAssetHotspotFloatingPopup } from "./PatrolAssetHotspotFloatingPopup.jsx";
import {
  hotspotHasOpenableContent,
  patrolHotspotUsesAnchoredPopup,
} from "./patrolAssetHotspotContent";

const SPHERE_R = 100;

function shouldShowAnchoredPopup(hotspot, openPopupIds) {
  const id = hotspot?.id;
  if (!id || !openPopupIds?.has(id)) return false;
  const showType = String(hotspot.showType ?? "").trim().toLowerCase();
  if (!patrolHotspotUsesAnchoredPopup(showType)) return false;
  return hotspotHasOpenableContent(hotspot);
}

function unmountPopupEntry(entry) {
  if (!entry?.popupRoot) return;
  entry.popupRoot.unmount();
  entry.popupMount?.remove();
  entry.popupMount = null;
  entry.popupRoot = null;
}

function createMarkerWrapper() {
  const el = document.createElement("div");
  el.className = "patrol-asset-hotspot-marker";
  Object.assign(el.style, {
    position: "absolute",
    pointerEvents: "none",
    transform: "translate(-50%, -50%)",
  });
  return el;
}

/**
 * World-locked asset hotspots on patrol immersive 360° (same projection as preset grid).
 */
export function PatrolAssetHotspotsOverlay({
  enabled,
  hotspots = [],
  canvasRef,
  cameraRef,
  hostRef,
  expandedId = null,
  openPopupIds = null,
  onHotspotPress,
  onClosePopup,
}) {
  const markerElsRef = useRef(new Map());
  const apiUrlRootsRef = useRef(new Map());
  const hotspotsRef = useRef(hotspots);
  const expandedIdRef = useRef(expandedId);
  const openPopupIdsRef = useRef(openPopupIds);
  const onPressRef = useRef(onHotspotPress);
  const onClosePopupRef = useRef(onClosePopup);

  hotspotsRef.current = hotspots;
  expandedIdRef.current = expandedId;
  openPopupIdsRef.current = openPopupIds;
  onPressRef.current = onHotspotPress;
  onClosePopupRef.current = onClosePopup;

  useEffect(() => {
    if (!enabled) return undefined;

    const host = hostRef.current;
    if (!host) return undefined;

    const markerLayer = document.createElement("div");
    markerLayer.className = "patrol-asset-hotspots-layer";
    Object.assign(markerLayer.style, {
      position: "absolute",
      inset: "0",
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: "12",
    });
    host.appendChild(markerLayer);

    const removeMarkerEntry = (id, entry) => {
      unmountPopupEntry(entry);
      const apiEntry = apiUrlRootsRef.current.get(id);
      if (apiEntry) {
        apiEntry.root.unmount();
        apiEntry.mount.remove();
        apiUrlRootsRef.current.delete(id);
      }
      entry.el.remove();
      markerElsRef.current.delete(id);
    };

    const syncMarkers = (camera, canvas) => {
      const list = hotspotsRef.current;
      const activeIds = new Set(list.map((h) => h.id));

      for (const [id, entry] of [...markerElsRef.current.entries()]) {
        if (!activeIds.has(id)) {
          removeMarkerEntry(id, entry);
        }
      }

      for (const h of list) {
        const showType = String(h.showType ?? h.show_type ?? "")
          .trim()
          .toLowerCase();
        const isApiUrl = showType === "api_url";
        const yawDeg = Number(h.yawDeg ?? h.yaw_deg);
        const pitchDeg = Number(h.pitchDeg ?? h.pitch_deg);
        const world = new THREE.Vector3();
        patrolHotspotWorldPosition(yawDeg, pitchDeg, SPHERE_R, world);

        let entry = markerElsRef.current.get(h.id);
        if (!entry) {
          const wrapper = createMarkerWrapper();
          if (isApiUrl) {
            const mount = document.createElement("div");
            mount.style.pointerEvents = "auto";
            wrapper.appendChild(mount);
            const root = createRoot(mount);
            apiUrlRootsRef.current.set(h.id, { root, mount });
            markerLayer.appendChild(wrapper);
            entry = {
              el: wrapper,
              btn: null,
              iconImg: null,
              mount,
              popupMount: null,
              popupRoot: null,
            };
          } else {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "patrol-asset-hotspot-marker-btn";
            btn.addEventListener("pointerdown", (ev) => ev.stopPropagation());
            btn.addEventListener("pointerup", (ev) => {
              ev.stopPropagation();
              onPressRef.current?.(h);
            });
            wrapper.appendChild(btn);
            markerLayer.appendChild(wrapper);
            entry = {
              el: wrapper,
              btn,
              iconImg: null,
              mount: null,
              popupMount: null,
              popupRoot: null,
            };
          }

          markerElsRef.current.set(h.id, entry);
        }

        const markerEl = entry.el;
        const showPopup = shouldShowAnchoredPopup(h, openPopupIdsRef.current);

        if (showPopup) {
          if (!entry.popupMount) {
            const popupMount = document.createElement("div");
            Object.assign(popupMount.style, {
              position: "absolute",
              left: "50%",
              bottom: "100%",
              transform: "translateX(-50%)",
              marginBottom: "10px",
              pointerEvents: "auto",
              zIndex: "25",
            });
            markerEl.insertBefore(popupMount, markerEl.firstChild);
            entry.popupMount = popupMount;
            entry.popupRoot = createRoot(popupMount);
          }
          entry.popupRoot.render(
            <PatrolAssetHotspotFloatingPopup
              anchored
              hotspot={h}
              onClose={() => onClosePopupRef.current?.(h.id)}
            />,
          );
        } else {
          unmountPopupEntry(entry);
        }

        if (isApiUrl) {
          const rootEntry = apiUrlRootsRef.current.get(h.id);
          if (rootEntry) {
            rootEntry.root.render(
              <PatrolApiUrlHotspotToggle
                contentUrl={String(h.contentUrl ?? h.content_url ?? "")}
              />,
            );
          }
          const lanes = getConfiguredApiUrlHotspotLanes(
            String(h.contentUrl ?? h.content_url ?? ""),
          );
          const width = patrolApiUrlMarkerWidth(lanes.length);
          Object.assign(markerEl.style, {
            width: `${width}px`,
            height: `${PATROL_API_URL_MARKER_HEIGHT_PX}px`,
          });
        } else if (entry.btn) {
          applyPatrolHotspotCircleMarkerStyle(entry.btn, {
            selected:
              expandedIdRef.current === h.id || showPopup,
            zIndex: "12",
          });
          entry.btn.style.position = "relative";
          entry.btn.style.left = "auto";
          entry.btn.style.top = "auto";
          entry.btn.style.transform = "none";
          const iconUrl = getPatrolHotspotMarkerIconUrl(showType);
          if (!entry.iconImg) {
            entry.iconImg = createPatrolHotspotIconImg(iconUrl);
            entry.btn.replaceChildren(entry.iconImg);
          } else if (entry.iconImg.src !== iconUrl) {
            entry.iconImg.src = iconUrl;
          }
          const label = h.assetName || h.assetItem || "Asset hotspot";
          entry.btn.title = label;
          entry.btn.setAttribute("aria-label", label);
        }

        const proj = projectPatrolWorldToHostPx(canvas, camera, host, world);
        if (!proj.visible) {
          markerEl.style.display = "none";
          continue;
        }

        markerEl.style.display = isApiUrl ? "block" : "flex";
        markerEl.style.left = `${proj.left}px`;
        markerEl.style.top = `${proj.top}px`;
        markerEl.style.zIndex = showPopup ? "20" : "12";
      }
    };

    let raf = 0;
    const tick = () => {
      const canvas = canvasRef.current;
      const camera = cameraRef.current;
      if (canvas && camera) {
        syncMarkers(camera, canvas);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      for (const [id, entry] of markerElsRef.current.entries()) {
        removeMarkerEntry(id, entry);
      }
      apiUrlRootsRef.current.clear();
      markerLayer.remove();
    };
  }, [enabled, canvasRef, cameraRef, hostRef, hotspots]);

  return null;
}
